import { useEffect, useState } from "react";
import { lazy, Suspense, useRef } from "react";
import { isPrerendered } from "./prerendered.js";
const FeaturedShowcaseLazy = lazy(() => import("./FeaturedShowcase.jsx"));
const GalleryCloudinaryLazy = lazy(() => import("./GalleryCloudinary"));

function Navbar() {
  return (
    <nav className="navbar navbar-expand-lg bg-white fixed-top shadow-sm">
      <div className="container">
        <a className="navbar-brand d-flex align-items-center" href="#home">
          <img
            src="25px.jpg"
            alt="Dalhousie Kennels"
            height="60"
            className="me-2"
          />
          <span>Dalhousie Kennels</span>
        </a>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navMenu"
          aria-controls="navMenu"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navMenu">
          <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
            <li className="nav-item">
              <a className="nav-link" href="#about">
                About
              </a>
            </li>
            <li className="nav-item">
              <a className="nav-link" href="#showcase">
                Showcase
              </a>
            </li>
            <li className="nav-item">
              <a className="nav-link" href="#gallery">
                Gallery
              </a>
            </li>
            <li className="nav-item">
              <a className="nav-link" href="#contact">
                Contact
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <header id="home" className="hero">
      <div className="container py-5">
        <div className="heroZ col-12 col-lg-8">
          <h1 className="eyebrow mb-2 d-inline-block headline">
            Dalhousie Kennels
          </h1>
          <p className="lead py-2 mb-4">
            Home of world class Boston Terriers with top European bloodlines.
          </p>
        </div>
        <div className="row d-flex cta-group heroZ pt-5">
          <div className="col">
            <a
              href="#contact"
              className="btn btn-primary btn-md w-100 me-2 shadow-soft"
            >
              Contact Us
            </a>
          </div>
          <div className="col-6 m-auto">
            <a href="#gallery" className="btn btn-secondary btn-md w-100">
              View Dogs
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

function About() {
  return (
    <section id="about" className="section-dark">
      <div className="container">
        <div className="row align-items-center g-4">
          <div className="col-md-6">
            <img
              className="img-fluid rounded shadow-soft"
              src="/about.jpg"
              alt="Best Puppy in Group - Drogo at Dalhousie Kennels"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="col-md-6">
            <div className="mb-5 section-title">
              <h2 className="eyebrow fw-bold">About</h2>
            </div>
            <p>
              Jack grew up with Boston Terriers and has loved caring for them
              ever since. He and his wife, Donna, carried that passion into
              their own kennel, Dalhousie Kennels.
            </p>
            <p>
              We're based in Niagara, Ontario, and we take pride in breeding
              champions. We show our world class imported Bostons in both Canada
              and the USA, and we are home to the top Boston Terriers in Canada.
            </p>
            <p>
              We breed for quality, temperament and health. Our dogs are raised
              in a family environment with plenty of love and attention, and our
              happy, healthy puppies make wonderful companions and show dogs.
              Many of our Boston Terriers have championship titles.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  // simple spam protection: honeypot + optional timing
  const [formReadyAt] = useState(() => Date.now());
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const formValid =
    form.name.trim().length > 0 &&
    form.email.trim().length > 0 &&
    form.message.trim().length > 0;

  const onSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    try {
      setSubmitting(true);
      const formData = new FormData(event.target);
      formData.append("access_key", "20d8ecd2-a326-4c4b-a3bf-32ca77b28ba9");

      const hp = formData.get("contact-me-check");
      const tookMs = Date.now() - formReadyAt;
      formData.append("_t", String(tookMs));
      if (hp) {
        setSubmitted(true);
        event.target.reset();
        setForm({ name: "", email: "", message: "" });
        return;
      }

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = { success: false };
      }
      if (response.ok && data.success) {
        setSubmitted(true);
        event.target.reset();
        setForm({ name: "", email: "", message: "" });
        setError(null);
      } else {
        setSubmitted(false);
        setError(
          "There was an error submitting the form. Please try again, or email us directly at dalhousiekennels@gmail.com."
        );
      }
    } catch (e) {
      setSubmitted(false);
      setError(
        "Network error submitting the form. Please try again later or email us directly at dalhousiekennels@gmail.com."
      );
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <section id="contact">
      <div className="container">
        <div className="row g-4">
          <div className="col-12 section-title">
            <h2 className="eyebrow fw-bold">Contact</h2>
            <p className="text-muted">
              We’d love to hear from you! Reach out with any questions.
            </p>
          </div>
          <div className="col-12 mb-4">
            <div className="row g-3 contact-tiles">
              <div className="col-12 col-md-4">
                <div className="contact-tile shadow-soft h-100">
                  <div className="tile-icon bg-accent-subtle">
                    <i className="bi bi-envelope-heart"></i>
                  </div>
                  <div className="tile-body">
                    <div className="tile-title">Email</div>
                    <a
                      className="tile-link"
                      href="mailto:dalhousiekennels@gmail.com"
                    >
                      dalhousiekennels@gmail.com
                    </a>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="contact-tile shadow-soft h-100">
                  <div className="tile-icon bg-accent-subtle">
                    <i className="bi bi-geo-alt"></i>
                  </div>
                  <div className="tile-body">
                    <div className="tile-title">Based In</div>
                    {/* link to google maps here */}
                    <a
                      className="tile-link"
                      href="https://maps.app.goo.gl/VaArKWkif8uDCrF69"
                    >
                      Niagara, Ontario, Canada
                    </a>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="contact-tile shadow-soft h-100">
                  <div className="tile-icon bg-accent-subtle">
                    <i className="bi bi-send-check"></i>
                  </div>
                  <div className="tile-body">
                    <div className="tile-title">Response Time</div>
                    <div className="text-muted">
                      Typically within 1 to 2 business days
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row g-4">
          <div className="col-12 mx-auto">
            <div className="card contact-card shadow-soft">
              <div className="card-body">
                <h5 className="card-title">Send a Message</h5>
                <p className="text-muted small mb-3">
                  We typically respond within 1 to 2 business days.
                </p>
                {error && (
                  <div
                    className="alert alert-danger alert-dismissable mb-3"
                    role="alert"
                  >
                    {error}
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="alert"
                      aria-label="Close"
                    ></button>
                  </div>
                )}
                {submitted ? (
                  <div className="alert alert-success mb-0">
                    Thanks! We will get back to you shortly.
                  </div>
                ) : (
                  <form id="contact-form" onSubmit={onSubmit}>
                    <input
                      type="checkbox"
                      name="contact-me-check"
                      tabIndex={-1}
                      value={1}
                      autoComplete="off"
                      style={{ display: "none" }}
                      aria-hidden="true"
                    />
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label" htmlFor="contact-name">
                          Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="contact-name"
                          className="form-control"
                          autoComplete="name"
                          placeholder="Your full name"
                          value={form.name}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, name: e.target.value }))
                          }
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" htmlFor="contact-email">
                          Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="contact-email"
                          className="form-control"
                          autoComplete="email"
                          placeholder="you@example.com"
                          value={form.email}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, email: e.target.value }))
                          }
                          required
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label" htmlFor="contact-message">
                          Message
                        </label>
                        <textarea
                          name="message"
                          id="contact-message"
                          className="form-control"
                          rows="5"
                          autoComplete="off"
                          placeholder="How can we help?"
                          value={form.message}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, message: e.target.value }))
                          }
                          required
                        ></textarea>
                      </div>
                      <div className="col-12 d-grid">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={!formValid || submitting}
                          aria-disabled={!formValid || submitting}
                        >
                          {submitting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              />
                              Sending…
                            </>
                          ) : (
                            <>
                              <i className="bi bi-send-check"></i> Send
                            </>
                          )}
                        </button>
                      </div>
                      <div className="col-12">
                        <p className="text-muted small mb-0">
                          Your message is sent through Web3Forms (
                          <a
                            href="https://web3forms.com/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            privacy policy
                          </a>
                          ). Rather not use the form? Email us at{" "}
                          <a href="mailto:dalhousiekennels@gmail.com">
                            dalhousiekennels@gmail.com
                          </a>
                          .
                        </p>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ onOpenPrivacy }) {
  const year = new Date().getFullYear();
  return (
    <footer className="footer">
      <div className="container d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
        <div className="small">&copy; {year} Dalhousie Kennels</div>
        <div className="d-flex align-items-center gap-3 small">
          <a
            className="footer-link small"
            type="button"
            onClick={onOpenPrivacy}
          >
            Privacy & Cookies
          </a>
          <span className="text-muted">•</span>
          <div className="small">
            <span>Website by </span>
            <a className="footer-link" type="button" href="https://chrismcg.ca">
              chrismcg.
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  // Auto-collapse on anchor clicks by triggering the toggler (no Bootstrap API needed)
  useEffect(() => {
    const links = Array.from(
      document.querySelectorAll(".navbar a.nav-link, .navbar a.navbar-brand")
    );
    const toggler = document.querySelector(
      '.navbar .navbar-toggler[data-bs-target="#navMenu"]'
    );
    const onClick = () => {
      if (!toggler) return;
      const isExpanded = toggler.getAttribute("aria-expanded") === "true";
      // Only trigger collapse when the menu is currently open
      if (isExpanded) toggler.click();
    };
    links.forEach((a) => a.addEventListener("click", onClick));
    return () => links.forEach((a) => a.removeEventListener("click", onClick));
  }, []);
  // Lightweight intersection helper to only mount heavy sections when visible.
  // Prerendered pages already contain these sections, so mount them immediately.
  const showcaseRef = useRef(null);
  const galleryRef = useRef(null);
  const [showShowcase, setShowShowcase] = useState(isPrerendered);
  const [showGallery, setShowGallery] = useState(isPrerendered);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            if (e.target === showcaseRef.current) setShowShowcase(true);
            if (e.target === galleryRef.current) setShowGallery(true);
          }
        }
      },
      { rootMargin: "300px 0px" }
    );
    if (showcaseRef.current) obs.observe(showcaseRef.current);
    if (galleryRef.current) obs.observe(galleryRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const navLinks = Array.from(
      document.querySelectorAll('.navbar .nav-link[href^="#"]')
    );
    if (!navLinks.length) return;

    const setActive = (id) => {
      navLinks.forEach((a) => {
        const isActive = a.getAttribute("href") === `#${id}`;
        a.classList.toggle("active", isActive);
        if (isActive) a.setAttribute("aria-current", "page");
        else a.removeAttribute("aria-current");
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { rootMargin: "0px 0px -60% 0px", threshold: [0.25, 0.5] }
    );

    const targets = Array.from(
      document.querySelectorAll("header#home, section[id]")
    );
    targets.forEach((el) => observer.observe(el));

    const onClick = (e) => {
      const href = e.currentTarget.getAttribute("href");
      if (href && href.startsWith("#")) setActive(href.slice(1));
    };
    navLinks.forEach((a) => a.addEventListener("click", onClick));

    // initial
    if (window.location.hash) setActive(window.location.hash.slice(1));
    else setActive("home");

    return () => {
      observer.disconnect();
      navLinks.forEach((a) => a.removeEventListener("click", onClick));
    };
  }, []);

  const [showPrivacy, setShowPrivacy] = useState(false);
  useEffect(() => {
    if (!showPrivacy) return;
    const onKey = (e) => e.key === "Escape" && setShowPrivacy(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [showPrivacy]);

  return (
    <>
      <Navbar />
      <Hero />
      <About />
      <div ref={showcaseRef}>
        {showShowcase ? (
          <Suspense
            fallback={
              <section id="showcase">
                <div className="container">
                  <div className="text-muted">Loading…</div>
                </div>
              </section>
            }
          >
            <FeaturedShowcaseLazy />
          </Suspense>
        ) : (
          <section id="showcase">
            <div className="container">
              <div className="text-muted">Loading…</div>
            </div>
          </section>
        )}
      </div>
      <div ref={galleryRef}>
        {showGallery ? (
          <Suspense
            fallback={
              <section id="gallery" className="section-dark">
                <div className="container">
                  <div className="text-muted">Loading…</div>
                </div>
              </section>
            }
          >
            <GalleryCloudinaryLazy />
          </Suspense>
        ) : (
          <section id="gallery" className="section-dark">
            <div className="container">
              <div className="text-muted">Loading…</div>
            </div>
          </section>
        )}
      </div>
      <Contact />
      <Footer onOpenPrivacy={() => setShowPrivacy(true)} />

      {showPrivacy && (
        <div
          className="dk-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacyTitle"
          onClick={() => setShowPrivacy(false)}
        >
          <div
            className="dk-modal shadow-soft"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-start">
              <h2 id="privacyTitle" className="h4 m-0">
                Privacy & Cookies
              </h2>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setShowPrivacy(false)}
              ></button>
            </div>
            <div className="mt-3">
              <p className="text-muted small mb-2">
                Last updated November 3, 2026
              </p>
              <h3 className="h6 mt-3">Contact form</h3>
              <p className="mb-2">
                Your name, email and message are sent through{" "}
                <a
                  href="https://web3forms.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Web3Forms
                </a>{" "}
                and arrive as an email at dalhousiekennels@gmail.com. We only
                use them to reply to you.
              </p>
              <h3 className="h6 mt-3">Services we use</h3>
              <p className="mb-2">
                The site runs on{" "}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Firebase
                </a>
                , and photos load through{" "}
                <a
                  href="https://cloudinary.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cloudinary
                </a>{" "}
                and{" "}
                <a
                  href="https://www.cloudflare.com/privacypolicy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cloudflare
                </a>
                . Like most web services, they may see your IP address and
                basic browser details when the page loads.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
